<script setup lang="ts">
// Enter confirms and Escape cancels, wherever focus is (the autofocus
// attribute does nothing on an element inserted after page load). With
// typed, the action waits until that text (a name) is typed: for what
// cannot be undone.
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { trapTab } from '../focus'

const props = defineProps<{ title: string; body?: string; confirmLabel?: string; danger?: boolean; typed?: string }>()
const emit = defineEmits<{ confirm: []; cancel: [] }>()
const ok = ref<HTMLButtonElement>()
const field = ref<HTMLInputElement>()
const typedText = ref('')
const ready = computed(() => !props.typed || typedText.value.trim() === props.typed.trim())

const box = ref<HTMLElement>()
function onKey(e: KeyboardEvent) {
    if (trapTab(e, box.value)) {
        e.stopPropagation() // a drawer under the dialog does not move it
        return
    }
    if (e.key === 'Escape') {
        e.preventDefault() // a drawer under the dialog stays open
        e.stopPropagation()
        emit('cancel')
        return
    }
    if (e.key !== 'Enter' || e.repeat) return
    if (e.target instanceof HTMLButtonElement && e.target !== ok.value) return // Enter on Cancel cancels
    e.preventDefault() // no second click from Enter on the focused OK button
    if (ready.value) emit('confirm')
}
onMounted(() => {
    ;(field.value ?? ok.value)?.focus()
    window.addEventListener('keydown', onKey, true) // before the drawers' handlers
})
onUnmounted(() => window.removeEventListener('keydown', onKey, true))
</script>

<template>
    <div class="fixed inset-0 z-20 flex items-center justify-center bg-black/55 scrim" @click.self="emit('cancel')">
        <div
            ref="box"
            class="max-h-[85vh] max-w-[min(720px,96vw)] min-w-[min(360px,92vw)] popover overflow-auto px-5 py-4"
            role="dialog"
            aria-modal="true"
            :aria-label="title"
        >
            <h3 class="mt-0 mb-1.5 text-[15px] font-semibold">{{ title }}</h3>
            <p v-if="body" class="mt-0 mb-3.5 text-fg-2">{{ body }}</p>
            <slot />
            <label v-if="typed" class="mb-3.5 block text-xs text-fg-2"
                >Type <b class="text-fg">{{ typed }}</b> to confirm<input
                    ref="field"
                    v-model="typedText"
                    class="mt-1"
                    autocomplete="off"
                    :aria-label="`Type ${typed} to confirm`"
            /></label>
            <div class="flex items-center justify-end gap-2">
                <button @click="emit('cancel')">Cancel</button>
                <button
                    ref="ok"
                    :class="danger ? 'btn-danger' : 'btn-primary'"
                    :disabled="!ready"
                    @click="ready && emit('confirm')"
                >
                    {{ confirmLabel ?? 'Confirm' }}
                </button>
            </div>
        </div>
    </div>
</template>
